import { request } from "$/lib/api"
import {
  Route,
  UpdateUpsellDiscount,
  UpsellCategory,
  UpsellDiscount,
  UpsellProduct,
  UpsertUpsellProduct,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

const upsellQueries = {
  all: () => ["upsell"],
  categories: () =>
    queryOptions({
      queryKey: [...upsellQueries.all(), "categories"],
      queryFn: () =>
        request<UpsellCategory[]>({
          url: Route.UPSELL__CATEGORIES,
          method: "GET",
        }),
    }),
  products: (storeId: string) =>
    queryOptions({
      queryKey: [...upsellQueries.all(), "products", storeId],
      queryFn: () =>
        request<UpsellProduct[]>({
          url: populateRoute(Route.UPSELL__STORE_PRODUCTS, storeId),
          method: "GET",
        }),
    }),
  discount: (storeId: string) =>
    queryOptions({
      queryKey: [...upsellQueries.all(), "discount", storeId],
      queryFn: () =>
        request<UpsellDiscount>({
          url: populateRoute(Route.UPSELL__STORE_DISCOUNT, storeId),
          method: "GET",
        }),
    }),
}

export default upsellQueries

export function useCreateUpsellProduct(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertUpsellProduct) =>
      request<UpsellProduct>({
        url: populateRoute(Route.UPSELL__STORE_PRODUCTS, storeId),
        method: "POST",
        data,
      }),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: upsellQueries.products(storeId).queryKey,
      }),
  })
}

export function useUpdateUpsellProduct(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpsertUpsellProduct }) =>
      request<UpsellProduct>({
        url: populateRoute(Route.UPSELL__STORE_PRODUCT, storeId, id),
        method: "PUT",
        data,
      }),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: upsellQueries.products(storeId).queryKey,
      }),
  })
}

export function useDeleteUpsellProduct(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      request<void>({
        url: populateRoute(Route.UPSELL__STORE_PRODUCT, storeId, id),
        method: "DELETE",
      }),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: upsellQueries.products(storeId).queryKey,
      }),
  })
}

export function useUploadUpsellProductImage(storeId: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append("image", file)
      return request<{ imageUrl: string }>({
        url: populateRoute(Route.UPSELL__STORE_PRODUCT_IMAGE, storeId),
        method: "POST",
        data: formData,
      })
    },
  })
}

export function useUpdateUpsellDiscount(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateUpsellDiscount) =>
      request<UpsellDiscount>({
        url: populateRoute(Route.UPSELL__STORE_DISCOUNT, storeId),
        method: "PUT",
        data,
      }),
    onSuccess: (discount) =>
      queryClient.setQueryData(
        upsellQueries.discount(storeId).queryKey,
        discount,
      ),
  })
}
